import axios from 'axios';
import { safeJsonTransformResponse } from '../utils/axiosResponseTransform';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8001/api',
    headers: {
        'Accept': 'application/json'
    },
    timeout: 20000, // 20 seconds timeout
    transformResponse: safeJsonTransformResponse,
});

// Simple in-memory cache for idempotent GET requests
// Keyed by full URL (including query) and Authorization header
const responseCache = new Map();
const inflightRequests = new Map();
const DEFAULT_TTL_MS = 30 * 1000; // 30 seconds

function buildCacheKey(config) {
    const url = new URL((config.baseURL || '') + config.url, window.location.origin);
    // Axios may keep params separately; include them in the key deterministically
    if (config.params) {
        const params = new URLSearchParams(config.params);
        // Sort params for stable ordering
        const sorted = new URLSearchParams();
        [...params.entries()].sort(([aKey], [bKey]) => aKey.localeCompare(bKey)).forEach(([k, v]) => sorted.append(k, v));
        url.search = sorted.toString();
    }
    const auth = (config.headers && (config.headers.Authorization || config.headers.authorization)) || '';
    return `${config.method || 'get'}|${auth}|${url.toString()}`;
}

function getCachedResponse(cacheKey) {
    const entry = responseCache.get(cacheKey);
    if (!entry) return null;
    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
        responseCache.delete(cacheKey);
        return null;
    }
    return entry.response;
}

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        
        if (process.env.NODE_ENV === 'development') {
            console.log('API Request:', {
                url: config.url,
                method: config.method,
                hasToken: !!token,
            });
        }
        
        // Don't set Content-Type for FormData requests
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Lightweight GET cache and request de-duplication
        const method = (config.method || 'get').toLowerCase();
        const shouldUseCache = method === 'get' && (config.cache !== false); // opt-out with cache:false
        if (shouldUseCache) {
            const ttl = typeof config.cacheTTL === 'number' ? config.cacheTTL : DEFAULT_TTL_MS;
            const cacheKey = buildCacheKey(config);

            // Return cached response if valid
            const cached = getCachedResponse(cacheKey);
            if (cached) {
                // Simulate axios response by resolving a promise here
                return Promise.reject({ __fromCache: true, cachedResponse: cached });
            }

            // If an identical request is in-flight, piggyback on it
            if (inflightRequests.has(cacheKey)) {
                return inflightRequests.get(cacheKey);
            }

            // Wrap the config promise so interceptors can short-circuit
            const resolver = Promise.resolve(config);
            inflightRequests.set(cacheKey, resolver);
            config.__cache = { cacheKey, ttl };
        }
        return config;
    },
    (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

function clearGetCache() {
    const size = responseCache.size;
    responseCache.clear();
    inflightRequests.clear();
    return size;
}

// Response interceptor
api.interceptors.response.use(
    (response) => {
        const method = (response.config.method || 'get').toLowerCase();
        if (['put', 'patch', 'post', 'delete'].includes(method)) {
            clearGetCache();
        }
        // Store in cache if eligible
        if (method === 'get' && response.config.__cache) {
            const { cacheKey, ttl } = response.config.__cache;
            responseCache.set(cacheKey, {
                response: response,
                timestamp: Date.now(),
                ttl
            });
            inflightRequests.delete(cacheKey);
        }
        return response;
    },
    (error) => {
        // Short-circuit for cached responses
        if (error && error.__fromCache && error.cachedResponse) {
            return Promise.resolve(error.cachedResponse);
        }
        // Improved error handling for network/timeout
        if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
            error.message = 'Cannot connect to server. Please check your network or try again later.';
        }
        console.error('Response error:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        
        if (error.response?.status === 401) {
            // Let callers decide how to handle auth; do not hard-redirect here
            // Optionally clear stale token
            localStorage.removeItem('token');
        }
        if (error.response?.status === 403 && error.response?.data?.code === 'inactive_gallery_only') {
            window.dispatchEvent(new CustomEvent('auth:inactive-gallery-only'));
        }
        return Promise.reject(error);
    }
);

export default api; 